import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth.js';

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    path: '/auth/impersonate',
    name: 'ImpersonateAuth',
    component: () => import('@/views/ImpersonateAuthView.vue'),
    meta: { public: true },
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/components/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/dashboard' },

      { path: 'dashboard', name: 'Dashboard',
        component: () => import('@/views/DashboardView.vue') },

      // ── CRM ───────────────────────────────────────────────
      { path: 'customers', name: 'Customers',
        component: () => import('@/views/CustomersView.vue'),
        meta: { permission: 'customers.view' } },
      { path: 'customers/:id', name: 'CustomerDetail',
        component: () => import('@/views/CustomerDetailView.vue'),
        meta: { permission: 'customers.view' } },

      // ── Fahrzeuge ─────────────────────────────────────────
      { path: 'vehicles', name: 'Vehicles',
        component: () => import('@/views/VehiclesView.vue'),
        meta: { permission: 'vehicles.view' } },

      // ── Maschinen ─────────────────────────────────────────
      { path: 'machines', name: 'Machines',
        component: () => import('@/views/MachinesView.vue'),
        meta: { permission: 'machines.view' } },

      // ── Aufmaß ────────────────────────────────────────────
      { path: 'aufmass', name: 'Aufmass',
        component: () => import('@/views/AufmassView.vue'),
        meta: { permission: 'aufmass.view' } },
      { path: 'aufmass/:id', name: 'AufmassDetail',
        component: () => import('@/views/AufmassDetailView.vue'),
        meta: { permission: 'aufmass.view' } },

      // ── Projekte ──────────────────────────────────────────
      { path: 'projects', name: 'Projects',
        component: () => import('@/views/ProjectsView.vue'),
        meta: { permission: 'projects.view' } },
      { path: 'projects/:id', name: 'ProjectDetail',
        component: () => import('@/views/ProjectDetailView.vue'),
        meta: { permission: 'projects.view' } },
      { path: 'media', name: 'ProjectMedia',
        component: () => import('@/views/ProjectMediaView.vue'),
        meta: { permission: 'time.view_own' } },

      // ── Baustellenplanung ─────────────────────────────────
      { path: 'schedule', name: 'Schedule',
        component: () => import('@/views/ScheduleView.vue'),
        meta: { permission: 'schedule.view' } },
      { path: 'carpentry-calc', name: 'CarpentryCalcOverview',
        component: () => import('@/views/CarpentryCalcOverviewView.vue'),
        meta: { requiresManager: true } },
      { path: 'carpentry-calc/roof', name: 'CarpentryCalcRoof',
        component: () => import('@/views/CarpentryCalcView.vue'),
        props: { templateId: 'roof' },
        meta: { requiresManager: true } },
      { path: 'carpentry-calc/abbund', name: 'CarpentryCalcAbbund',
        component: () => import('@/views/CarpentryCalcView.vue'),
        props: { templateId: 'abbund' },
        meta: { requiresManager: true } },
      { path: 'carpentry-calc/montage', name: 'CarpentryCalcMontage',
        component: () => import('@/views/CarpentryCalcView.vue'),
        props: { templateId: 'montage' },
        meta: { requiresManager: true } },
      { path: 'carpentry-calc/schalung', name: 'CarpentryCalcSchalung',
        component: () => import('@/views/CarpentryCalcView.vue'),
        props: { templateId: 'schalung' },
        meta: { requiresManager: true } },
      { path: 'carpentry-calc/daemmung', name: 'CarpentryCalcDaemmung',
        component: () => import('@/views/CarpentryCalcView.vue'),
        props: { templateId: 'daemmung' },
        meta: { requiresManager: true } },
      { path: 'carpentry-calc/dachlattung', name: 'CarpentryCalcDachlattung',
        component: () => import('@/views/CarpentryCalcView.vue'),
        props: { templateId: 'dachlattung' },
        meta: { requiresManager: true } },
      { path: 'carpentry-calc/dachdeckung', name: 'CarpentryCalcDachdeckung',
        component: () => import('@/views/CarpentryCalcView.vue'),
        props: { templateId: 'dachdeckung' },
        meta: { requiresManager: true } },
      // ── Material / Lager ──────────────────────────────────
      { path: 'material', name: 'Material',
        component: () => import('@/views/MaterialView.vue'),
        meta: { permission: 'material.view' } },
      { path: 'inventory', name: 'Inventory',
        component: () => import('@/views/InventoryView.vue'),
        meta: { permission: 'material.manage' } },
      { path: 'suppliers', name: 'Suppliers',
        component: () => import('@/views/SupplierView.vue'),
        meta: { permission: 'material.view' } },

      // ── Billing ───────────────────────────────────────────
      { path: 'offers', name: 'Offers',
        component: () => import('@/views/DocumentListView.vue'),
        props: { docType: 'offer' },
        meta: { permission: 'documents.view' } },
      { path: 'offers/new', name: 'NewOffer',
        component: () => import('@/views/DocumentFormView.vue'),
        props: { docType: 'offer' },
        meta: { permission: 'documents.create' } },
      { path: 'offers/:id', name: 'OfferDetail',
        component: () => import('@/views/DocumentDetailView.vue'),
        meta: { permission: 'documents.view' } },
      { path: 'offers/:id/edit', name: 'EditOffer',
        component: () => import('@/views/DocumentFormView.vue'),
        props: r => ({ docType: 'offer', editId: r.params.id }),
        meta: { permission: 'documents.edit' } },
      { path: 'invoices', name: 'Invoices',
        component: () => import('@/views/DocumentListView.vue'),
        props: { docType: 'invoice' },
        meta: { permission: 'documents.view' } },
      { path: 'invoices/new', name: 'NewInvoice',
        component: () => import('@/views/DocumentFormView.vue'),
        props: { docType: 'invoice' },
        meta: { permission: 'documents.create' } },
      { path: 'invoices/:id', name: 'InvoiceDetail',
        component: () => import('@/views/DocumentDetailView.vue'),
        meta: { permission: 'documents.view' } },
      { path: 'invoices/:id/edit', name: 'EditInvoice',
        component: () => import('@/views/DocumentFormView.vue'),
        props: r => ({ docType: 'invoice', editId: r.params.id }),
        meta: { permission: 'documents.edit' } },
      { path: 'credit-notes', name: 'CreditNotes',
        component: () => import('@/views/DocumentListView.vue'),
        props: { docType: 'credit_note' },
        meta: { permission: 'documents.view' } },
      { path: 'credit-notes/new', name: 'NewCreditNote',
        component: () => import('@/views/DocumentFormView.vue'),
        props: { docType: 'credit_note' },
        meta: { permission: 'documents.create' } },
      { path: 'credit-notes/:id', name: 'CreditNoteDetail',
        component: () => import('@/views/DocumentDetailView.vue'),
        meta: { permission: 'documents.view' } },
      { path: 'credit-notes/:id/edit', name: 'EditCreditNote',
        component: () => import('@/views/DocumentFormView.vue'),
        props: r => ({ docType: 'credit_note', editId: r.params.id }),
        meta: { permission: 'documents.edit' } },

      // ── Zeiterfassung ─────────────────────────────────────
      { path: 'time', name: 'Time',
        component: () => import('@/views/TimeRecordingView.vue'),
        meta: { permission: 'time.view_own' } },
      { path: 'chat', name: 'Chat',
        component: () => import('@/views/ChatView.vue') },
      { path: 'settings', name: 'Settings',
        component: () => import('@/views/SettingsView.vue'),
        meta: { permission: 'settings.manage' } },
      { path: 'payroll', name: 'Payroll',
        component: () => import('@/views/PayrollView.vue'),
        meta: { permission: 'payroll.view' } },
      { path: 'report', name: 'Report',
        component: () => import('@/views/ReportView.vue'),
        meta: { permission: 'reports.view' } },
      { path: 'sick-leave', name: 'SickLeave',
        component: () => import('@/views/SickLeaveView.vue'),
        meta: { permission: 'vacation.request' } },
      { path: 'vacation', name: 'Vacation',
        component: () => import('@/views/VacationView.vue'),
        meta: { permission: 'vacation.request' } },

      // ── Verwaltung ────────────────────────────────────────
      { path: 'permissions', name: 'Permissions',
        component: () => import('@/views/PermissionsView.vue'),
        meta: { permission: 'employees.manage' } },
      { path: 'employees', name: 'Employees',
        component: () => import('@/views/EmployeeView.vue'),
        meta: { permission: 'employees.manage' } },
      // ── Changelog ──────────────────────────────────────────
      { path: 'changelog', name: 'Changelog',
        component: () => import('@/views/ChangelogView.vue'),
        meta: { permission: 'changelog.view' } },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0, left: 0, behavior: 'auto' };
  },
});

router.beforeEach((to) => {
  const auth = useAuthStore();

  if (!to.meta.public && !auth.isLoggedIn) return { name: 'Login' };
  if (to.name === 'Login' && auth.isLoggedIn) return { name: 'Dashboard' };

  // Permission-Check
  if (to.meta.permission && auth.isLoggedIn) {
    if (!auth.can(to.meta.permission)) {
      return { name: 'Dashboard' };
    }
  }

  // Manager-or-higher check
  if (to.meta.requiresManager && auth.isLoggedIn) {
    if (!auth.isManager) {
      return { name: 'Dashboard' };
    }
  }
});

export default router;